import { useCallback, useEffect, useState } from 'react';

/**
 * Carrega dados do painel de administração com estado de carga, erro e
 * recarga manual.
 *
 * Todas as abas do painel repetiam o mesmo trio `isLoading/error/dados` com o
 * mesmo try/catch; centralizar aqui evita que uma delas esqueça de tratar o
 * erro e mostre a tela vazia como se não houvesse acesso nenhum — que é
 * exatamente a leitura errada num painel de monitoração.
 *
 * @param loader Função que busca os dados. Precisa ser estável (useCallback).
 * @param refreshMs Recarga automática, em milissegundos. 0 desliga.
 */
export function useAdminData<T>(loader: () => Promise<T>, refreshMs = 0) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      setData(await loader());
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Falha ao carregar os dados.');
    } finally {
      setIsLoading(false);
    }
  }, [loader]);

  useEffect(() => {
    void reload();

    if (refreshMs <= 0) return;

    const timer = setInterval(() => void reload(), refreshMs);
    return () => clearInterval(timer);
  }, [reload, refreshMs]);

  return { data, error, isLoading, reload };
}
